import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useMeasure from 'react-use-measure';
import { nanoid } from 'nanoid';

import { RootState } from 'src/redux/store';

import Reveal from 'src/components/animation';
import { BannerView } from 'src/components/banner';

import { fetchCollectionSharedContacts } from 'src/redux/actions/collections';

import { AssetsLayoutEnum, MenuState, ModuleEnum } from './types';

import { DealRoomContext, DealRoomContextProps } from './components/context';

import { AssetViewerSelector, EditMenu, Menubar, ViewMenu } from './components/menu';
import { useSpringRef, useTransition, animated, useChain } from '@react-spring/web';
import { calculateUpdateRequest, shouldNotifyOnChange } from 'src/utils/dealRoom.helper';
import { NotifyChangesDialog } from './components/live';
import { AssetCatergory, DealRoomChangeRequest, StakeHolderTypeEnum } from 'src/types/dealRoom';
import { BannerEditor } from './components/banner/library';
import { BannerFilters } from './components/banner/filter';
import { CropEditor } from './components/crop';
import Toast from 'src/components/toast';
import Slider from 'src/components/slider';
import { Crop } from 'src/components/editThumbnail/crop';
import { Media, MediaFile } from 'src/types/media';
import Button from 'src/components/button';

import { parseMediaMetadata } from 'src/utils/media.service';

import _ from 'lodash';
import cx from "classnames";
import styles from './styles.module.scss';
import { editDealRoom, fetchDealRoom, fetchDealRoomAssets, fetchDealRoomEngageChatUsers, fetchDealRoomSmartCards, fetchStakeholders, invalidateDealRoomUpdationState, invalidateEditDealRoom, updateDealRoom, updateDealRoomProperties } from 'src/redux/actions/dealRoom';
import { uploadMedia } from 'src/redux/actions/media';
import DealSlider from './components/dealSlider';
import ComposeDialog from 'src/components/compose/components/dialog';
import { fetchConversation } from 'src/redux/actions/conversation';
import userRoleService from 'src/utils/userRoles.service';
import subscriptionPlanService from 'src/utils/subscriptionPlan.service';
import { DEALROOM_ASSET_VIEWERS, DEFAULT_ASSET_VIEWER } from 'src/constants/collections';
import Panel from '../asset/components/animation/panel';
import { fetchIntegrations } from 'src/legacy/redux/actions/integrations';
import { useDealRoomAdoption } from 'src/redux/actions/adoption/dealRoom';
import { DEAL_ROOM } from 'src/constants/adoption/sourceTypes';
import { IUseFetchProps, useFetch } from 'src/components/hooks/useFetch';
import runInSequenceWithInterval from 'src/utils/runInSequence';


const CalendarImage = "/images/calendar.svg";

const DealRoom: React.FC = () => {

  const { id, sectionId } = useParams();

  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const location = useLocation();

  const [menuState, setMenuState] = useState<MenuState>({
    primaryAction: 'view'
  });
  const [activeAssetLayout, setActiveAssetLayout] = useState<AssetsLayoutEnum>(AssetsLayoutEnum.LIST);
  const [showOverlay, setShowOverlay] = useState(false);

  const editing = menuState.primaryAction === 'edit';
  const isBannerEditing = menuState.primaryAction === 'edit-banner'

  const account = useSelector((state: RootState) => _.get(state, "accounts.item"));
  const user = useSelector((state: any) => _.get(state, 'users.me'));
  const loading = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${id}'].loading`, false));
  const loaded = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${id}'].loaded`, false));
  const dealId = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${id}'].item.opportunity`, ''));
  const paperfliteEmailDisabled = useSelector((state: any) => {
    const paperfliteEmailServerEnabled = _.get(state, 'account.item.paperfliteEmailServerEnabled', true);
    return !paperfliteEmailServerEnabled;
  });
  const updated = useSelector((state: any) => _.get(state, `dealRooms.update['${id}'].updated`, false));
  const updating = useSelector((state: any) => _.get(state, `dealRooms.update['${id}'].updating`, false));
  const errorMessage = useSelector((state: any) => _.get(state, `dealRooms.dealRoom.[${id}].error.errors`, false));

  const persistedDealRoom = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${id}'].item`);
  });

  const chatUsersData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom.${id}.engage.chatUsers`, null);
  });

  const chatUsers = _.get(chatUsersData, 'items', []);

  const dealRoom = useSelector((state: any) => {
    const editDealRoom = _.get(state, `dealRooms.edit.dealRoom`);
    if (editDealRoom) {
      return editDealRoom;
    }

    return _.get(state, `dealRooms.dealRoom['${id}'].item`);
  });

  const persistedDealRoomAssets = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${id}'].assets[${AssetCatergory.INTERNAL}]`);
  });


  const dealRoomAssets = useSelector((state: any) => {
    const editDealRoom = _.get(state, `dealRooms.edit`);
    if (editing) {
      return _.get(editDealRoom, `allAssets[${AssetCatergory.INTERNAL}]`);
    }

    return _.get(state, `dealRooms.dealRoom['${id}'].assets[${AssetCatergory.INTERNAL}]`);
  })

  const allAssetsData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${id}'].assets`);
  });

  const updateDealRoomRequestProps: IUseFetchProps = {
    action: updateDealRoom,
    loading: updating,
    succeeded: updated,
    response: dealRoom,
    error: null,
  };
  const {
    trackDealRoomAddMeetingLinkViewed,
    trackDealRoomEdited,
    trackDealRoomOverviewClicked,
    trackDealRoomAssetsClicked,
    trackDealRoomUsersClicked,
    trackDealRoomEngageClicked,
    trackDealRoomActivityClicked,
    trackDealRoomMeetingLinkAdded,
    trackDealRoomShared,
    trackDealRoomOverviewAdded
  } = useDealRoomAdoption();
  const { handleFetch: updateDealRoomRequest } = useFetch(updateDealRoomRequestProps);

  useEffect(() => {
    const dealRoomChanges = calculateUpdateRequest(
      {
        ...persistedDealRoom,
        assets: _.get(persistedDealRoomAssets, "items", [])
      },
      {
        ...dealRoom,
        assets: _.get(dealRoomAssets, "items", [])
      },
      true
    );
    
    if (!_.isEmpty(dealRoomChanges)) {
      setShowUnsavedChangesToast(true);
    } else {
      setShowUnsavedChangesToast(false);
    }
  }, [persistedDealRoom, persistedDealRoomAssets, dealRoom, dealRoomAssets]);
  
  
  const [customerLogoMedia, setCustomerLogoMedia] = useState<Media | null>(null);
  const [companyLogoMedia, setCompanyLogoMedia] = useState<Media | null>(null);
  const [showAddMeetingLink, setShowAddMeetingLink] = useState<boolean>(false);
  const [meetingLink, setMeetingLink] = useState<string>('');
  const [showDealInsights, setShowDealInsights] = useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [assetViewer, setAssetViewer] = React.useState(DEFAULT_ASSET_VIEWER);
  const [showUnsavedChangesToast,setShowUnsavedChangesToast] = useState(false);

  const handleAssetViewerChange = (viewer: string) => {
    setAssetViewer(viewer);
  };

  const toggleDealInsights = () => {
    setShowDealInsights(prevState => !prevState);
  }

  const customerLogoMediaState = useSelector((state: any) => ({
    uploading: _.get(state, `media.${customerLogoMedia?.id}.loading`, false),
    uploaded: _.get(state, `media.${customerLogoMedia?.id}.loaded`, false),
    item: _.get(state, `media.${customerLogoMedia?.id}.item`, null),
  }));

  const companyLogoMediaState = useSelector((state: any) => ({
    uploading: _.get(state, `media.${companyLogoMedia?.id}.loading`, false),
    uploaded: _.get(state, `media.${companyLogoMedia?.id}.loaded`, false),
    item: _.get(state, `media.${companyLogoMedia?.id}.item`, null),
  }));



  const showContent = menuState.primaryAction === 'view' || menuState.primaryAction === 'edit';
  const showBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === null;
  const cropBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'crop';
  const showFilters = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'filter';
  const showSelectAssetViewer = menuState.primaryAction === 'edit' && menuState.secondaryAction === 'asset-viewer';
  const shareId = _.get(dealRoom, "shareId", "");

  const [customerName, setCustomerName] = useState<string>('');

  const banner = _.get(dealRoom, 'banner');

  const handleCustomerNameChange = (e) => {
    handleEditClick();
    setCustomerName(e.target.value)
    updateDealRoomProperty("customerName", e.target.value);
  }

  useEffect(() => {
    handleRedirectUrl();
    setShowOverlay(false);
  }, [location])

  useEffect(() => {
    if (shareId) {
      dispatch(fetchConversation(shareId));
    }
  }, [shareId])

  useEffect(() => {

    if (!customerLogoMediaState.uploading && customerLogoMediaState.uploaded) {
      updateDealRoomProperty("customerLogo", customerLogoMediaState.item);
    }
  }, [customerLogoMediaState.uploaded, customerLogoMediaState.uploading]);

  useEffect(() => {

    if (!companyLogoMediaState.uploading && companyLogoMediaState.uploaded) {
      updateDealRoomProperty("companyLogo", companyLogoMediaState.item);
    }
  }, [companyLogoMediaState.uploaded, companyLogoMediaState.uploading]);


  useEffect(() => {
    if (dealRoom) {
      setCustomerName(dealRoom.customerName);
      setMeetingLink(dealRoom.meetingLink)
      setAssetViewer(_.get(dealRoom, "assetViewer", DEFAULT_ASSET_VIEWER));
    }
  }, [dealRoom])

  const obj = ["overview", "assets", "people", "action-plan", "activity", "engage"]
  const pathname = _.get(location, "pathname");
  const activeModule = _.find(obj, view => _.includes(pathname, view))
  const activeBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const activeModuleIndex = obj.indexOf(activeModule || "overview");
    if (activeBarRef.current) {
      const activeBar = activeBarRef.current;
      if (activeBar) {
        activeBar.style.top = `${activeModuleIndex * 53.5}px`;
      }
    }

  })

  useEffect(() => {
    if (id) {
      dispatch(fetchDealRoom({ id }));
      dispatch(fetchStakeholders({ dealRoomId: id, type: StakeHolderTypeEnum.INTERNAL, page: 0 }));
      dispatch(fetchStakeholders({ dealRoomId: id, type: StakeHolderTypeEnum.EXTERNAL, page: 0 }));
      dispatch(fetchDealRoomEngageChatUsers({
        dealRoomId: id,
        page: 0
      }));
    }

    return () => {
      dispatch(invalidateEditDealRoom(id!));
      dispatch(invalidateDealRoomUpdationState());
    }
  }, [id])


  useEffect(() => {
    if (updated && menuState.primaryAction === 'edit') {
      setMenuState({ primaryAction: 'view' });
      if (id) {
        dispatch(fetchDealRoomAssets({
          id: id,
          page: 0,
          limit: 20,
          assetCategory: AssetCatergory.INTERNAL
        }))
        dispatch(fetchDealRoomAssets({
          id: id,
          page: 0,
          limit: 20,
          assetCategory: AssetCatergory.ALL
        }))
      }

      dispatch(invalidateEditDealRoom(id!));
      dispatch(invalidateDealRoomUpdationState());
    }
  }, [menuState.primaryAction, updated]);

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, [])

  useEffect(() => {
    if (id) {
      dispatch(fetchDealRoomAssets({
        id: id,
        page: 0,
        limit: 20,
        assetCategory: AssetCatergory.INTERNAL
      }))
    }
  }, [id]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('edit') === 'true' && menuState.primaryAction !== 'edit') {
      setMenuState({ primaryAction: 'edit' });
      dispatch(editDealRoom({
        dealRoom: dealRoom,
        assets: dealRoomAssets,
        allAssets: allAssetsData
      }));
    }
  }, []);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (menuState.primaryAction === 'edit') {
      dispatch(editDealRoom({
        dealRoom: dealRoom,
        assets: dealRoomAssets,
        allAssets: allAssetsData
      }));
    }
    else if (menuState.primaryAction === 'view') {
      dispatch(invalidateEditDealRoom(id!));
      setCustomerName(_.get(dealRoom, "customerName", ""));
      if (!sectionId) return;

      const activeSection = parseInt(sectionId || '0');
      const activeIndex = validateSectionIndex(activeSection, _.size(_.get(persistedDealRoom, 'sections', [])));

      if (!_.isEqual(sectionId, activeIndex)) {
        if (!activeIndex) {
          navigate(`/deal-room/${id}/assets/internal`, {
            replace: true
          });
        } else {
          navigate(`/deal-room/${id}/assets/internal/sections/${activeIndex}`, {
            replace: true
          });
        }
      }
    }

  }, [loaded, menuState.primaryAction]);

  useEffect(() => {
    switch (menuState.secondaryAction) {
      case 'save': {
        handleSave();
        setMenuState(state => ({ ...state, secondaryAction: null }));
        break;
      }
      case 'reset': {
        if (menuState.primaryAction === 'edit') {
          setMenuState(state => ({
            ...state,
            primaryAction: 'view',
            secondaryAction: null
          }));
        }

        break;
      }
      default:
        break;
    }
  }, [menuState.secondaryAction]);

  useEffect(() => {
    if (!showAddMeetingLink) return;

    trackDealRoomAddMeetingLinkViewed({ source_cta: DEAL_ROOM });    
  }, [showAddMeetingLink]);


  const handleRedirectUrl = () => {
    const obj = ["overview", "assets", "people", "activity", "engage", "action-plan"]
    const pathname = _.get(location, "pathname");
    const currentPath = _.find(obj, view => _.includes(pathname, view))
    if (!currentPath) {
      navigate(`/deal-room/${id}/overview`)
    }
  }

  const validateSectionIndex = (index: number, sections: number) => {
    if (sections == 0) {
      return undefined;
    } else if (index < 0 || index >= sections) {
      return "0";
    } else {
      return "" + index;
    }
  }

  const handleSave = () => {

    handleSaveConfirm(false, '', '');
  }

  const handleSaveConfirm = (notifyRecipients, subject, message) => {
    const updatedPesistedDealRoom = {
      ...persistedDealRoom,
      assets: _.get(persistedDealRoomAssets, "items", [])
    }

    const updatedDealRoom = {
      ...dealRoom,
      assets: _.get(dealRoomAssets, "items", [])
    }
    const changes: DealRoomChangeRequest = calculateUpdateRequest(updatedPesistedDealRoom, updatedDealRoom);

    changes.notifyRecipients = notifyRecipients;
    changes.subject = subject;
    changes.message = message;

    updateDealRoomRequest({
      id: _.get(dealRoom, "id", ""),
      request: changes,
    }).then((response) => {
      trackDealRoomEdited(_.get(response, "id", ""));

      if (Object.hasOwn(changes, "description")) {
        trackDealRoomOverviewAdded();
      }
    })
    toast.dismiss();
    setShowUnsavedChangesToast(false);
  }

  const handleMenuChange = (updatedMenuState: any) => {
    setMenuState(updatedMenuState);
  }

  const handleActiveAssetLayoutChange = (updatedActiveAssetLayoutChange: AssetsLayoutEnum) => {
    setActiveAssetLayout(updatedActiveAssetLayoutChange);
  }

  const handleBack = () => {
    if (activeModule === ModuleEnum.ACTION_PLAN && editing) {
      toast(
        <Toast.Failure
          heading="Psst!"
          message="You have unsaved changes. Save before leaving to keep your updates."
        />,
        {
          position: "bottom-right",
          autoClose: false,
          onClose: () => {}
        }
      );

      return;
    }

    if ((editing || isBannerEditing) && showUnsavedChangesToast) {
      toast(
        <Toast.warning
          heading="Warning"
          message="You have unsaved changes. Save before leaving to keep your updates."
        />,
        {
          position: "bottom-right",
          autoClose: false,
          onClose: () => {
            setShowUnsavedChangesToast(false);
          }
        }
      );
      setShowUnsavedChangesToast(false);
      return;
    }
    toast.dismiss();
    navigate('/deal-rooms');
  };
  
  const updateDealRoomProperty = (property: string, value: any) => {
    dispatch(updateDealRoomProperties({
      dealRoom: dealRoom.id!,
      property: property,
      value: value
    }));
  }

  const getNotificationContentForConversation = () => {
    return {
      subject: `${dealRoom?.name || ""} was updated`,
      message: `<span>Hello there,</span><br/><br/>We've recorded some changes to the content collection
      ${dealRoom?.name || ""} that was shared with you by <a href='mailto:${user.email}'>${user.email}</a>.<br/><br/>`
    }
  }

  const handleGetRecipient = (recipient) => {
    return recipient
  }

  const handleFetchRecipientsForPage = (page) => {
    dispatch(fetchCollectionSharedContacts({
      collection: id!,
      page: page,
      limit: 10,
    }));
  }

  const contentRef = useSpringRef();
  const contentTransitions = useTransition(showContent, {
    ref: contentRef,
    initial: { opacity: 1 },
    from: { opacity: 0, top: '30vh' },
    enter: { opacity: 1, top: '0px' },
    leave: { opacity: 0, top: '30vh' },
  });


  const bannerEditorRef = useSpringRef();
  const bannerEditorTransitions = useTransition(showBanner, {
    ref: bannerEditorRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const bannerFilterRef = useSpringRef();
  const bannerFilterTransitions = useTransition(showFilters, {
    ref: bannerFilterRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const cropperRef = useSpringRef();
  const cropperTransitions = useTransition(cropBanner, {
    ref: cropperRef,
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: showBanner ? 0 : 0,
    config: {
      friction: 14,
      tension: 120,
    },
  });

  useChain([contentRef, bannerEditorRef, bannerFilterRef, cropperRef], [0, 0, 0, 0, 0]);

  const handleNavigateClick = (module: ModuleEnum) => {
    if (disableSidemenu) {
      return;
    }
    
    const path = module === ModuleEnum.PEOPLE || module === ModuleEnum.ASSETS ? 'internal' : '';

    if(module === ModuleEnum.ASSETS){ // If assets module, check for sections
      const sectionsCount = _.size(dealRoomContextState.dealRoom?.sections);
      if (sectionsCount > 0) {
        navigate(`/deal-room/${id}/${module}/internal/sections/0`); // redirect to first section
        return;
      }
    }
    
    navigate(`/deal-room/${id}/${module}${path ? `/${path}` : ''}`);
  }

  const handleEditBannerClick = () => {
    handleMenuChange({
      ...menuState,
      primaryAction: 'edit-banner',
      secondaryAction: null
    })
  }

  const handleMenuStateChange = (value) => {
    setMenuState(value);
  }

  const accountLogo = _.get(account, "logo.thumbnail", "");
  const companyLogo = _.get(dealRoom, "companyLogo.full", accountLogo);
  const customerLogo = _.get(dealRoom, "customerLogo.full", "");

  const handleImageUpload = async (property, value) => {

    if (property === "customerLogo") {
      setCustomerLogoMedia(value)
    }
    else {
      setCompanyLogoMedia(value)
    }

    const selectedMedia = value;
    const mediaFile = selectedMedia as MediaFile;
    const blob = await fetch(selectedMedia!.full!).then(r => r.blob());
    const file = new File([blob], 'thumbnail.png', { type: mediaFile.contentType || blob.type });

    dispatch(uploadMedia({
      id: selectedMedia?.id as string,
      media: {
        ...selectedMedia,
        file: file
      } as MediaFile
    }))
  }

  const handleShareClick = () => {
    setSharing(true);
  }

  const assetViewers = useMemo(() => {
    return subscriptionPlanService
      .getCollectionAssetViewers()
      .map((viewerId) => {
        return DEALROOM_ASSET_VIEWERS.find((item) => item.id === viewerId);
      })
      .filter((item) => item);
  }, [subscriptionPlanService.getCollectionAssetViewers()]);

  const handleAddAssetClick = () => {
    if (activeModule === ModuleEnum.ASSETS) {
      handleMenuChange({
        primaryAction: 'edit',
        secondaryAction: 'add-asset'
      })
      dispatch(editDealRoom({
        dealRoom: dealRoom,
        assets: dealRoomAssets,
        allAssets: allAssetsData
      }))
    }
    else {
      runInSequenceWithInterval([
        () => handleNavigateClick(ModuleEnum.ASSETS),
        () => {
          handleMenuChange({
            primaryAction: 'edit',
            secondaryAction: 'add-asset'
          })
          dispatch(editDealRoom({
            dealRoom: dealRoom,
            assets: dealRoomAssets,
            allAssets: allAssetsData
          }))
        }], 500)
    }

  }

  const handleAddUsersClick = () => {
    runInSequenceWithInterval([
      () => navigate(`/deal-room/${id}/people/external`),
      () => {
        handleShareClick();
      }], 500)
  }

  const hasContents = _.get(dealRoom, "allAssetsCount", 0) > 0;

  const disableSidemenu = activeModule === ModuleEnum.ACTION_PLAN && menuState.primaryAction === "edit";

  const dealRoomContextState = {
    id: id,
    dealRoom: dealRoom || {},
    dealRoomAssets: dealRoomAssets || {},
    activeModule: activeModule ?? null,
    entityType: 'dealRoom',
    editSelector: `dealRooms.edit.dealRoom`,
    menuState: menuState,
    onMenuChange: handleMenuChange,
    isDealRoomLoading: loading,
    updateDealRoomProperty: updateDealRoomProperty,
    recipientSelector: `dealRooms.dealRoom['${id}'].recipients`,
    getNotificationContent: getNotificationContentForConversation,
    getRecipient: handleGetRecipient,
    updateSelector: `dealRooms.update['${id}']`,
    fetchRecipientsForPage: handleFetchRecipientsForPage,
    itemSelector: `dealRooms.dealRoom['${id}'].item`,
    dealId,
    handleShareClick: handleShareClick,
    hasContents,
    activeAssetLayout: activeAssetLayout,
    onActiveAssetLayoutChange: handleActiveAssetLayoutChange,
    showDealDetails: () => setShowDealInsights(true),
    handleAddAssetClick,
    handleAddUsersClick
  }

  const handleEditClick = () => {
    handleMenuChange({
      primaryAction: 'edit'
    })
    dispatch(editDealRoom({
      dealRoom: dealRoom,
      assets: dealRoomAssets,
      allAssets: allAssetsData
    }))
  }

  if (loading && !dealRoom) {
    return <div className={styles.loading}>Loading deal room...</div>
  }

  if (!dealRoom) {
    return <div className={styles.loading}>Deal room not found</div>
  }

  if (!userRoleService.canAccessDealRoom()) {
    return <div className={styles.loading}>Access Denied</div>
  }

  return (
    <div className={styles.dealRoomContainer}>

      <DealRoomContext.Provider value={dealRoomContextState}>
        <Menubar
          dealRoom={dealRoom}
          menuState={menuState}
          onChange={handleMenuStateChange}
        />
        <div className={styles.banner}>
          {(menuState.primaryAction === 'view' || menuState.primaryAction === 'edit') && <> <div className={styles.logoContainer}>
            <EditableLogo uploading={companyLogoMediaState.uploading} handleEditClick={handleEditClick} logo={companyLogo} editing={_.get(dealRoom, "canEdit", false)} handleImageUpload={(media) => handleImageUpload("companyLogo", media)} />
            <EditableLogo uploading={customerLogoMediaState.uploading} handleEditClick={handleEditClick} logo={customerLogo} editing={_.get(dealRoom, "canEdit", false)} alt={"Add buyer logo"} handleImageUpload={(media) => handleImageUpload("customerLogo", media)} />
          </div>
            <div className={styles.customerName}>
              Account :  <input type="text" disabled={!_.get(dealRoom, "canEdit", false)} placeholder='Add buyer name' value={customerName} onChange={handleCustomerNameChange} />
            </div> </>}
          {menuState.primaryAction === 'edit' && <i onClick={handleEditBannerClick} className={cx('paperflite-edit', styles.bannerEdit)}></i>}
          <BannerView banner={banner} defaultBanner="/images/collections/background.jpeg" />
        </div>

        <Reveal className={styles.backButtonContainer} show={true}>
          <button className={styles.backButton} onClick={handleBack}>
            <i className="paperflite-arrow-up"></i>
          </button>
        </Reveal>


        <div className={cx(styles.body, {
          [styles.cropping]: cropBanner
        })}>
          {contentTransitions((style, item) => (
            item && <animated.div style={style} className={cx(styles.contentContainer, styles.content)}>
              <div className={styles.sideMenu}>
                <div ref={activeBarRef} className={styles.activeBar} />
                <div className={cx(styles.tab, {
                  [styles.active]: activeModule === ModuleEnum.OVERVIEW,
                  [styles.disabled]: disableSidemenu,
                })} onClick={() => {
                  trackDealRoomOverviewClicked();
                  handleNavigateClick(ModuleEnum.OVERVIEW)
                }}>
                  <i className={"paperflite-categories"} />
                  <span className={styles.text}>Overview</span>
                </div>

                <div className={cx(styles.tab, {
                  [styles.active]: activeModule === ModuleEnum.ASSETS,
                  [styles.disabled]: disableSidemenu,
                })} onClick={() => {
                    trackDealRoomAssetsClicked();
                    handleNavigateClick(ModuleEnum.ASSETS)
                  }}>
                  <i className={"paperflite-multiple-documents"} />
                  <span className={styles.text}>Assets</span>
                </div>

                <div className={cx(styles.tab, {
                  [styles.active]: activeModule === ModuleEnum.PEOPLE,
                  [styles.disabled]: disableSidemenu,
                })} onClick={() => {
                    trackDealRoomUsersClicked();
                    handleNavigateClick(ModuleEnum.PEOPLE)
                  }}>
                  <i className={"paperflite-group"} />
                  <span className={styles.text}>Users</span>
                </div>
                
                <div className={cx(styles.tab, styles.zeroPadding, {
                  [styles.active]: activeModule === ModuleEnum.ACTION_PLAN
                })} onClick={() => {
                    // add adoption for Action Plan
                    handleNavigateClick(ModuleEnum.ACTION_PLAN)
                  }}>
                  <i className={"paperflite-action-plan"} />
                  <span className={styles.text}>Action Plan</span>
                </div>

                <div className={cx(styles.tab, {
                  [styles.active]: activeModule === ModuleEnum.ACTIVITY,
                  [styles.disabled]: disableSidemenu,
                })} onClick={() => {
                    trackDealRoomActivityClicked();
                    handleNavigateClick(ModuleEnum.ACTIVITY)
                  }}>
                  <i className={"paperflite-clock"} />
                  <span className={styles.text}>Activity</span>
                </div>

                {_.size(chatUsers) > 0 && <div className={cx(styles.tab, {
                  [styles.active]: activeModule === ModuleEnum.ENGAGE,
                  [styles.disabled]: disableSidemenu,
                })} onClick={() => {
                    trackDealRoomEngageClicked({ source_cta: DEAL_ROOM });
                    handleNavigateClick(ModuleEnum.ENGAGE)
                  }}>
                  <i className={"paperflite-engage"} />
                  <span className={styles.text}>Engage</span>
                </div>}
              </div>

              <div className={styles.mainContent}>
                <div className={styles.header}>
                  <div style={{ maxWidth: window.location.pathname.includes("engage") ? 250 : 600 }} className={styles.truncatedText}>{_.get(dealRoom, "name")}</div>

                  {(activeModule !== ModuleEnum.ENGAGE && activeModule !== ModuleEnum.ACTIVITY) && (
                    <div>
                      <ViewMenu
                        show={menuState.primaryAction === 'view'}
                        showAddMeetingLink={() => {
                          setShowAddMeetingLink(true)
                        }}
                      />

                      <EditMenu
                        menuState={menuState}
                        onMenuChange={handleMenuChange}
                      />
                    </div>
                  )}
                </div>
                <Outlet />
              </div>
            </animated.div>))
          }

          {bannerEditorTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <BannerEditor dealRoom={dealRoom} />
            </animated.div>
          ))}


          {bannerFilterTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <BannerFilters dealRoom={dealRoom} />
            </animated.div>
          ))}


          {cropperTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <CropEditor enabled={cropBanner}
                dealRoom={dealRoom}
                menuState={menuState}
                onMenuChange={handleMenuChange}
              />
            </animated.div>
          ))}
        </div>

        <Slider.Dialog show={showAddMeetingLink} width={"380px"} onClose={() => setShowAddMeetingLink(false)}>
          <Slider.Header
            title={<>attach a <br />meeting link</>}
          />
          <Slider.Body>
            <div className={styles.meetingLinkContainer}>
              <img src={CalendarImage} />
              <br />
              <input type="text" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="Add your meeting link here" />
              <p>Help prospects schedule time with you directly from the deal room. Add your scheduling link for easier collaboration.</p>
            </div>

          </Slider.Body>
          <Slider.Actions>
            <Button
              variant="primary"
              icon="paperflite-tick"
              text={"Save"}
              onClick={() => {
                trackDealRoomMeetingLinkAdded();
                updateDealRoomProperty("meetingLink", meetingLink);
                setShowAddMeetingLink(false);
              }}
              disabled={false}
            />
            <Button
              variant="secondary"
              text="Cancel"
              onClick={() => {
                setShowAddMeetingLink(false);
              }}
              disabled={false}
            />
          </Slider.Actions>
        </Slider.Dialog>

        <Panel show={showDealInsights} className={styles.dealSliderContainer}>
          {showDealInsights && (
            <DealSlider onClose={toggleDealInsights} />
          )}
        </Panel>

        <ComposeDialog
          show={sharing}
          dialog={true}
          initialContent={[{ ...dealRoom, assets: [..._.get(persistedDealRoomAssets, 'items', [])] }]}
          onClose={() => {
            setSharing(false);
            trackDealRoomShared({ dealRoomName: _.get(dealRoom, "name", "") });
            if (id) {
              dispatch(fetchStakeholders({ dealRoomId: id, type: StakeHolderTypeEnum.EXTERNAL, page: 0 }));
              dispatch(fetchDealRoomEngageChatUsers({
                dealRoomId: id,
                page: 0
              }));
            }
          }}
        />

        <Slider.Dialog show={showSelectAssetViewer} width={"380px"} onClose={() => {
          handleMenuChange({
            primaryAction: 'edit',
            secondaryAction: null
          });
        }}>
          <Slider.Header
            title={"make it pretty, please"}
          />
          <Slider.Body>
            <AssetViewerSelector
              viewers={assetViewers}
              selected={assetViewer}
              onSelect={handleAssetViewerChange}
            />
          </Slider.Body>
          <Slider.Actions>
            <Button
              variant="primary"
              icon="paperflite-tick"
              text={"Save"}
              onClick={() => {
                updateDealRoomProperty("assetViewer", assetViewer);
                handleMenuChange({
                  primaryAction: 'edit',
                  secondaryAction: null
                });
              }}
              disabled={false}
            />
            <Button
              variant="secondary"
              text="Cancel"
              onClick={() => {
                setAssetViewer(_.get(dealRoom, "assetViewer", DEFAULT_ASSET_VIEWER));
                handleMenuChange({
                  primaryAction: 'edit',
                  secondaryAction: null
                });
              }}
              disabled={false}
            />
          </Slider.Actions>
        </Slider.Dialog>

      </DealRoomContext.Provider>
    </div>

  );
}

interface IEditableLogoProps {
  logo: string,
  alt?: string,
  editing: boolean,
  handleEditClick: () => void,
  uploading: boolean,
  handleImageUpload: (media: MediaFile) => void
}

const EditableLogo: React.FC<IEditableLogoProps> = ({ logo, alt, uploading, editing, handleImageUpload, handleEditClick }) => {


  const _handleUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    const size = uploadedFile.size / 1000;
    if (size < 2000) {
      const file = e.target.files?.[0];
      if (!file) return;

      const thumbnailMedia = await parseMediaMetadata(file);
      if (!thumbnailMedia) return;

      const updatedThubnailMedia = {
        ...thumbnailMedia,
        id: nanoid(6),
      }
      handleImageUpload(updatedThubnailMedia);
    } else {
      toast(
        <Toast.Failure
          message={"File size should be less than 2MB"}
        />
      );
    }
  };



  return (
    <span className={cx(styles.logo, {
      [styles.logoEditing]: editing,
      [styles.emptyLogo]: !logo && !uploading
    })}>
      {!logo ? <span className={styles.placeholder}> {alt} </span> : <img src={logo} />}
      {(editing || !logo) && <input
        type="file"
        className="file-input-hidden"
        accept="image/*"
        onChange={_handleUpload}
        onClick={(e) => {
          (e.target as HTMLInputElement).value = "";
          handleEditClick();
        }}
      />}
      <span className={styles.editIcon}>
        <i className='paperflite-edit' />
      </span>
      {uploading && <i className={cx(styles.uploading, 'fa-solid fa-spinner fa-spin-pulse')} />}

    </span>
  )
}

export default DealRoom;